import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold">Cookie Policy</CardTitle>
            <CardDescription>
              Last updated: {new Date().toLocaleDateString()}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">What Are Cookies</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Cookies are small text files that are placed on your device when you visit our website. 
                  They help us provide you with a better experience by remembering your preferences and 
                  understanding how you use our service.
                </p>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Essential Cookies</h3>
                  <p className="text-muted-foreground">
                    These cookies are necessary for the website to function properly. They enable basic 
                    functions like page navigation, access to secure areas, and authentication.
                  </p>
                  <ul className="list-disc pl-6 mt-2 text-muted-foreground space-y-1">
                    <li>Session management</li>
                    <li>Security features</li>
                    <li>Authentication state</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Functional Cookies</h3>
                  <p className="text-muted-foreground">
                    These cookies allow the website to remember choices you make (such as your language 
                    preference or region) and provide enhanced, more personalized features.
                  </p>
                  <ul className="list-disc pl-6 mt-2 text-muted-foreground space-y-1">
                    <li>Language preferences</li>
                    <li>Theme settings (dark/light mode)</li>
                    <li>Cookie consent preferences</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Analytics Cookies</h3>
                  <p className="text-muted-foreground">
                    These cookies help us understand how visitors interact with our website by collecting 
                    and reporting information anonymously. This helps us improve our service.
                  </p>
                  <ul className="list-disc pl-6 mt-2 text-muted-foreground space-y-1">
                    <li>Usage statistics</li>
                    <li>Performance metrics</li>
                    <li>Error tracking (via Sentry)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Third-Party Cookies</h3>
                  <p className="text-muted-foreground">
                    Some cookies are placed by third-party services that appear on our pages. We don't 
                    control these cookies.
                  </p>
                  <ul className="list-disc pl-6 mt-2 text-muted-foreground space-y-1">
                    <li>Social media integration</li>
                    <li>Authentication providers</li>
                    <li>Analytics services</li>
                  </ul>
                </div>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">Managing Cookies</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  You can control and/or delete cookies as you wish. You can delete all cookies that are 
                  already on your computer and you can set most browsers to prevent them from being placed.
                </p>
                <p>
                  However, if you do this, you may have to manually adjust some preferences every time you 
                  visit our site, and some features may not work properly.
                </p>
                <div className="bg-accent/50 p-4 rounded-lg mt-4">
                  <p className="font-medium mb-2">Browser Cookie Settings:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                    <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                    <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                    <li><strong>Edge:</strong> Settings → Privacy → Cookies and site permissions</li>
                  </ul>
                </div>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">Your Consent</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  When you first visit our website, we'll show you a cookie consent banner. By clicking 
                  "Accept," you consent to our use of cookies as described in this policy.
                </p>
                <p>
                  You can change your cookie preferences at any time by clearing your browser cookies 
                  and revisiting our site, or by using your browser's cookie management tools.
                </p>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">EU Cookie Law Compliance</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  We comply with the EU Cookie Law (ePrivacy Directive) by:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Obtaining your consent before setting non-essential cookies</li>
                  <li>Providing clear information about the cookies we use</li>
                  <li>Giving you control over your cookie preferences</li>
                  <li>Respecting your right to withdraw consent</li>
                </ul>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">Changes to This Policy</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in our practices 
                  or for other operational, legal, or regulatory reasons.
                </p>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-primary">Contact Us</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  If you have questions about our use of cookies, please contact us at:
                </p>
                <p className="font-medium">
                  Email: privacy@yieldsocial.com
                </p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookiePolicy;
